// Thin wrapper around `@uiw/react-md-editor` that surfaces the GitLab
// Flavored Markdown features the issue body needs: headings, lists,
// task-list checkboxes (`- [ ] item`), bold/italic, inline + fenced
// code, tables, blockquotes, links. Split source/preview matches
// GitLab's issue-body editor.
//
// We hide the live preview by default (`preview: "edit"`) — the
// dedicated read-only `MarkdownView` is what the page renders elsewhere
// and most editing happens in source mode. The user can toggle to
// preview from the toolbar.

import { type JSX } from "react";
import MDEditor, { commands } from "@uiw/react-md-editor";

import { useT } from "../../i18n/index.js";
import { useResolvedTheme } from "../../theme/ThemeProvider.js";

import "@uiw/react-md-editor/markdown-editor.css";
import "@uiw/react-markdown-preview/markdown.css";

interface MarkdownEditorProps {
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly placeholder?: string;
  /** Height of the editor in pixels. The editor's own scrollbar appears past this. */
  readonly height?: number;
  readonly ariaLabel?: string;
}

// Toolbar selection roughly tracks GitLab's issue-body toolbar: text
// formatting, then structure (headings/lists/checklist/quote), then
// inserts (link/image/code/table). Each `commands.X` is a built-in icon
// + behaviour from @uiw/react-md-editor.
const TOOLBAR = [
  commands.bold,
  commands.italic,
  commands.strikethrough,
  commands.divider,
  commands.title,
  commands.unorderedListCommand,
  commands.orderedListCommand,
  commands.checkedListCommand,
  commands.quote,
  commands.divider,
  commands.link,
  commands.image,
  commands.code,
  commands.codeBlock,
  commands.table,
  commands.divider,
  commands.help,
];

const EXTRA = [commands.codeEdit, commands.codeLive, commands.codePreview];

export const MarkdownEditor = ({
  value,
  onChange,
  placeholder,
  height = 320,
  ariaLabel,
}: MarkdownEditorProps): JSX.Element => {
  const t = useT();
  // The editor's own colour mode, bound to the RESOLVED theme and never
  // "auto": @uiw's markdown.css carries an unguarded prefers-color-scheme
  // block, so "auto" would hand an OS-dark user who chose Light a dark editor.
  const resolvedTheme = useResolvedTheme();
  return (
  <div className="md-editor-wrap" data-color-mode={resolvedTheme}>
    <MDEditor
      value={value}
      onChange={(next) => onChange(next ?? "")}
      height={height}
      preview="edit"
      visibleDragbar={false}
      commands={TOOLBAR}
      extraCommands={EXTRA}
      textareaProps={{
        placeholder: placeholder ?? t("dashboard.markdown.placeholder"),
        "aria-label": ariaLabel ?? t("dashboard.markdown.ariaLabel"),
      }}
    />
  </div>
  );
};
